import { useSuspenseQuery } from "@tanstack/react-query";
import { Flex, PrimarySecondaryPage, WorkflowPage } from "@wayflyer/flyui";
import { useFeatureFlagEnabled } from "posthog-js/react";
import { useIntl } from "react-intl";
import { useCustomerAppConfig } from "undercarriage";
import { OnboardingStepper } from "onboarding";

import { DocumentUploadSupportingContent } from "../../../components";
import { getCustomerBusinessModelQueryOptions } from "../../../queries/customer-data/queries";
import { useDocumentQuery } from "../../../queries/document-uploads/queries";
import { useFundingApplication } from "../../../queries/funding-application/queries";
import { UploadDocumentType } from "../../../types";
import { getAccountingThreshold } from "../../../utils/funding-journey";
import {
  DocumentNavigationGroup,
  ManagementAccountDocumentUpload,
  useDocumentNavigationActions,
  MESSAGES,
} from "../components";

export const ManagementAccountsDocumentUploadPage = () => {
  const intl = useIntl();
  const isNewOnboardingFlowEnabled = useFeatureFlagEnabled("enable_new_onboarding_setup_flow");
  const {
    data: { customerDomain },
  } = useCustomerAppConfig();
  const { data: businessModel } = useSuspenseQuery(getCustomerBusinessModelQueryOptions(customerDomain));
  const { data: fundingApplicationData } = useFundingApplication(customerDomain);
  const { data: uploadedDocuments } = useDocumentQuery(customerDomain, fundingApplicationData.id);
  const hasUploadedDocuments = !!uploadedDocuments?.filter(
    (doc) => doc.documentType === UploadDocumentType.MANAGEMENT_ACCOUNTS
  ).length;

  const { primaryAction, secondaryAction } = useDocumentNavigationActions({
    forType: UploadDocumentType.MANAGEMENT_ACCOUNTS,
  });

  const requiredDuration =
    fundingApplicationData.requestedAmount &&
    fundingApplicationData.requestedAmountCurrency &&
    fundingApplicationData.requestedAmount >=
      4 * getAccountingThreshold(businessModel)[fundingApplicationData.requestedAmountCurrency]
      ? 24
      : 12;

  if (isNewOnboardingFlowEnabled) {
    return (
      <WorkflowPage
        variant="wide"
        titleSlot={<OnboardingStepper currentStep={4} />}
        title={intl.formatMessage(MESSAGES.managementAccountsTitle)}
        actions={{ primary: primaryAction, secondary: secondaryAction }}
      >
        <Flex maxWidth="550px">
          <ManagementAccountDocumentUpload duration={requiredDuration} />
        </Flex>
      </WorkflowPage>
    );
  }

  const PrimaryContent = (
    <Flex direction="column" gap="8">
      <ManagementAccountDocumentUpload duration={requiredDuration} />
      <DocumentNavigationGroup isStepComplete={hasUploadedDocuments} forType={UploadDocumentType.MANAGEMENT_ACCOUNTS} />
    </Flex>
  );

  return (
    <PrimarySecondaryPage
      titleSlot={<OnboardingStepper currentStep={4} />}
      showStepperBackground={true}
      title={intl.formatMessage(MESSAGES.managementAccountsTitle)}
      primaryContent={PrimaryContent}
      secondaryContent={<DocumentUploadSupportingContent />}
    />
  );
};
